'use client'

import { forwardRef, ReactNode } from 'react'
import { cn } from '@/lib/utils'

// Button variants
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link'

// Button sizes
export type ButtonSize = 'sm' | 'md' | 'lg'

// Button colors
export type ButtonColor = 'brand' | 'white' | 'dark' | 'gray' | 'success' | 'warning' | 'error'

// Icon positions
export type IconPosition = 'left' | 'right'

export interface ButtonProps {
  // Content
  children?: ReactNode
  text?: string

  // Icon
  icon?: ReactNode
  iconPosition?: IconPosition

  // Styling
  variant?: ButtonVariant
  size?: ButtonSize
  color?: ButtonColor
  className?: string

  // State
  disabled?: boolean
  loading?: boolean

  // Behavior
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  form?: string

  // Accessibility
  'aria-label'?: string
  'aria-describedby'?: string
}

// Color schemes for different variants and colors
const colorSchemes = {
  brand: {
    primary: 'bg-brand text-text-white hover:bg-brand/80 border-border-brand',
    secondary: 'bg-brand/10 text-text-brand hover:bg-brand/20 border-border-brand/20',
    outline: 'bg-transparent text-text-brand hover:bg-brand/10 border-border-brand',
    ghost: 'bg-transparent text-text-brand hover:bg-brand/10 border-transparent',
    link: 'bg-transparent text-text-brand hover:text-brand/80 border-transparent',
  },
  white: {
    primary: 'bg-white text-text-primary hover:bg-gray-100 border-white',
    secondary: 'bg-white/10 text-text-white hover:bg-white/20 border-white/20',
    outline: 'bg-transparent text-text-white hover:bg-white/10 border-white',
    ghost: 'bg-transparent text-text-white hover:bg-white/10 border-transparent',
    link: 'bg-transparent text-text-white hover:text-gray-200 border-transparent',
  },
  dark: {
    primary: 'bg-dark text-text-white hover:bg-dark/80 border-dark',
    secondary: 'bg-dark/10 text-text-primary hover:bg-dark/20 border-dark/20',
    outline: 'bg-transparent text-text-primary hover:bg-dark/10 border-dark',
    ghost: 'bg-transparent text-text-primary hover:bg-dark/10 border-transparent',
    link: 'bg-transparent text-text-primary hover:text-dark/80 border-transparent',
  },
  gray: {
    primary: 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600',
    secondary: 'bg-gray-600/10 text-gray-600 hover:bg-gray-600/20 border-gray-600/20',
    outline: 'bg-transparent text-gray-600 hover:bg-gray-600/10 border-gray-600',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-600/10 border-transparent',
    link: 'bg-transparent text-gray-600 hover:text-gray-700 border-transparent',
  },
  success: {
    primary: 'bg-green-600 text-white hover:bg-green-700 border-green-600',
    secondary: 'bg-green-600/10 text-green-600 hover:bg-green-600/20 border-green-600/20',
    outline: 'bg-transparent text-green-600 hover:bg-green-600/10 border-green-600',
    ghost: 'bg-transparent text-green-600 hover:bg-green-600/10 border-transparent',
    link: 'bg-transparent text-green-600 hover:text-green-700 border-transparent',
  },
  warning: {
    primary: 'bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-600',
    secondary: 'bg-yellow-600/10 text-yellow-600 hover:bg-yellow-600/20 border-yellow-600/20',
    outline: 'bg-transparent text-yellow-600 hover:bg-yellow-600/10 border-yellow-600',
    ghost: 'bg-transparent text-yellow-600 hover:bg-yellow-600/10 border-transparent',
    link: 'bg-transparent text-yellow-600 hover:text-yellow-700 border-transparent',
  },
  error: {
    primary: 'bg-red-600 text-white hover:bg-red-700 border-red-600',
    secondary: 'bg-red-600/10 text-red-600 hover:bg-red-600/20 border-red-600/20',
    outline: 'bg-transparent text-red-600 hover:bg-red-600/10 border-red-600',
    ghost: 'bg-transparent text-red-600 hover:bg-red-600/10 border-transparent',
    link: 'bg-transparent text-red-600 hover:text-red-700 border-transparent',
  },
}

// Size classes
const sizeClasses = {
  sm: 'text-sm px-3 py-2',
  md: 'text-base px-4 py-3',
  lg: 'text-lg px-6 py-4',
}

// Base button classes
const baseClasses =
  'inline-flex items-center justify-center gap-2 font-normal font-ui transition-all duration-300 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border'

// Button variants function for use in other components
export function buttonVariants(props?: {
  variant?: ButtonVariant
  size?: ButtonSize | 'icon' | 'default'
  color?: ButtonColor
}) {
  const variant = props?.variant || 'primary'
  const size = props?.size || 'md'
  const color = props?.color || 'brand'

  // Map 'icon' and 'default' sizes to our standard sizes
  let sizeClass = sizeClasses[size as keyof typeof sizeClasses]
  if (size === 'icon') {
    sizeClass = 'h-9 w-9'
  } else if (size === 'default') {
    sizeClass = sizeClasses['md']
  }

  const colorScheme = colorSchemes[color][variant]

  return cn(baseClasses, sizeClass, colorScheme)
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      text,
      icon,
      iconPosition = 'right',
      variant = 'primary',
      size = 'md',
      color = 'brand',
      className = '',
      disabled = false,
      loading = false,
      onClick,
      type = 'button',
      form,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    // Determine content to display
    const content = text || children

    // Get color scheme
    const colorScheme = colorSchemes[color][variant]

    // Build class names
    const buttonClasses = cn(
      baseClasses,
      sizeClasses[size],
      colorScheme,
      {
        'cursor-not-allowed': disabled || loading,
        'pointer-events-none': disabled || loading,
      },
      className,
    )

    // Loading spinner component
    const LoadingSpinner = () => (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )

    // Render content with icon
    const renderContent = () => {
      if (loading) {
        return (
          <>
            <LoadingSpinner />
            <span className="uppercase">{content}</span>
          </>
        )
      }

      if (!icon) {
        return <span className="uppercase">{content}</span>
      }

      if (iconPosition === 'left') {
        return (
          <>
            {icon}
            <span className="uppercase">{content}</span>
          </>
        )
      }

      return (
        <>
          <span className="uppercase">{content}</span>
          {icon}
        </>
      )
    }

    const buttonProps = {
      type,
      form,
      className: buttonClasses,
      disabled: disabled || loading,
      onClick,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      ...props,
    }

    return (
      <button {...buttonProps} ref={ref}>
        {renderContent()}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
